import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { outcomeTileSx } from './employerVerificationDashboardPresentation';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import {
  employerVerificationDashboardApi,
  type DashboardGateKey as ApiDashboardGateKey,
  type DashboardGateOutcome,
  type DashboardOutcomeTile,
  type EmployerVerificationListItem,
  type GateIssueFilter,
} from '../api/employerVerificationDashboardApi';
import { employerVerificationAnalyticsApi } from '../api/employerVerificationAnalyticsApi';
import { messageFromEmployerVerificationHttpError } from '../lib/employerVerificationHttpErrorMessage';
import { employerVerificationFinalChipColor } from '../lib/employerVerificationFinalChip';
import {
  findFirstFailingGateKey,
  GatePipelineStrip,
} from '../components/verification/GatePipelineStrip';
import { EmployerVerificationDetailPane } from '../components/verification/EmployerVerificationDetailPane';
import {
  dashboardColumns,
  finalOutcomeLabelStored,
  finalOutcomeTechnical,
  gateMeta,
  GATE_ORDER_PIPE,
  type GateKey,
} from '../lib/employerVerificationCopy';

const MS_DAY = 24 * 60 * 60 * 1000;

function rollingTrailing30dWindows() {
  const t = Date.now();
  return {
    current: {
      verifiedFrom: new Date(t - 30 * MS_DAY).toISOString(),
      verifiedTo: new Date(t).toISOString(),
    },
    prior: {
      verifiedFrom: new Date(t - 60 * MS_DAY).toISOString(),
      verifiedTo: new Date(t - 30 * MS_DAY).toISOString(),
    },
  };
}

function outcomeBucketShares(outcomes: Array<{ finalStatusBucket: string; count: number }>) {
  const total = outcomes.reduce((sum, o) => sum + (Number.isFinite(o.count) ? o.count : 0), 0);
  const countFor = (name: string) => {
    const u = name.toUpperCase();
    return outcomes.find((o) => String(o.finalStatusBucket ?? '').trim().toUpperCase() === u)?.count ?? 0;
  };
  const rejected = countFor('REJECTED');
  const manual = countFor('MANUAL_REVIEW');
  return {
    total,
    rejected,
    manual,
    rejectedShare: total > 0 ? rejected / total : 0,
    manualShare: total > 0 ? manual / total : 0,
  };
}

function listRowAccentSx(
  theme: Theme,
  row: EmployerVerificationListItem,
): Record<string, unknown> | undefined {
  const fs = row.finalStatus?.trim().toUpperCase();
  const anyFail = GATE_ORDER_PIPE.some((g) => row.gates[g]?.trim().toUpperCase() === 'FAIL');
  const anyRev = GATE_ORDER_PIPE.some((g) => row.gates[g]?.trim().toUpperCase() === 'REVIEW');
  if (fs === 'REJECTED') {
    return { '& > td': { bgcolor: alpha(theme.palette.error.main, 0.075) } };
  }
  if (anyFail) {
    return { '& > td': { bgcolor: alpha(theme.palette.error.main, 0.038) } };
  }
  if (anyRev) {
    return { '& > td': { bgcolor: alpha(theme.palette.warning.main, 0.065) } };
  }
  return undefined;
}

function statusToColor(theme: Theme, code: string | null | undefined) {
  const u = (code ?? '').trim().toUpperCase();
  if (u === 'PASS' || u === 'VERIFIED') return theme.palette.success.main;
  if (u === 'FAIL' || u === 'REJECTED') return theme.palette.error.main;
  if (u === 'REVIEW' || u === 'MANUAL_REVIEW') return theme.palette.warning.main;
  return alpha(theme.palette.text.secondary, 0.35);
}

function gateStatusChip(raw: string | null | undefined): {
  label: string;
  color: 'success' | 'error' | 'warning' | 'default';
} {
  const u = (raw ?? '').trim().toUpperCase();
  if (u === 'PASS') return { label: 'PASS', color: 'success' };
  if (u === 'FAIL') return { label: 'FAIL', color: 'error' };
  if (u === 'REVIEW') return { label: 'REVIEW', color: 'warning' };
  return { label: u || 'NOT_RUN', color: 'default' };
}

function GateResultChipsRow({
  row,
  onGateClick,
}: {
  row: EmployerVerificationListItem;
  onGateClick: (gate: GateKey) => void;
}) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {GATE_ORDER_PIPE.map((gate) => {
        const statusRaw = row.gates[gate];
        const chip = gateStatusChip(statusRaw);
        const gateReason = row.gateMessages?.[gate]?.trim();
        const gateFail = row.gateCheckpointAudit?.[gate]?.find(
          (cp) => cp.status.trim().toUpperCase() === 'FAIL',
        );
        const gateReview = row.gateCheckpointAudit?.[gate]?.find(
          (cp) => cp.status.trim().toUpperCase() === 'REVIEW',
        );
        const fallbackReason =
          gateFail?.reason ||
          gateReview?.reason ||
          'Click to open full checkpoint output for this gate.';

        return (
          <Tooltip
            key={`${row.id}-${gate}`}
            placement="top"
            arrow
            title={
              <Box sx={{ maxWidth: 360 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}
                >
                  {gateMeta[gate].title} · {chip.label}
                </Typography>
                <Typography variant="caption" sx={{ lineHeight: 1.45, display: 'block' }}>
                  {gateReason || fallbackReason}
                </Typography>
              </Box>
            }
          >
            <Chip
              size="small"
              label={`${gateMeta[gate].short} ${chip.label}`}
              color={chip.color}
              variant={chip.color === 'default' ? 'outlined' : 'filled'}
              onClick={() => onGateClick(gate)}
              sx={{ fontWeight: 800, cursor: 'pointer' }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

const OVERVIEW_OUTCOMES: DashboardGateOutcome[] = ['PASS', 'FAIL', 'REVIEW'];
type OverviewOutcome = DashboardGateOutcome;

type GateApplicantPreview = {
  id: string;
  applicationId: string;
  applicantId: string;
  ccid: string | null;
  employerLegalName: string | null;
  gateReason: string | null;
};

function normalizeOverviewOutcome(
  raw: string | null | undefined,
): OverviewOutcome | null {
  const u = (raw ?? '').trim().toUpperCase();
  if (u === 'PASS' || u === 'FAIL' || u === 'REVIEW') return u;
  return null;
}

function RowHoverInsight({ row }: { row: EmployerVerificationListItem }) {
  const hp = row.hoverPayload;
  const theme = useTheme();
  if (!hp) {
    return <Typography variant="caption">Hover data unavailable.</Typography>;
  }
  const pinned = findFirstFailingGateKey(row.gates);

  return (
    <Box sx={{ p: 0.75, maxWidth: 520 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          letterSpacing: 0.04,
          color: '#fff',
          display: 'block',
          mb: 0.75,
        }}
      >
        Checkpoints · same order as verifier
      </Typography>
      <Box sx={{ bgcolor: alpha('#000', 0.35), borderRadius: 1.5, p: 1, mb: 1 }}>
        <GatePipelineStrip
          gates={row.gates}
          gateCheckpointAudit={row.gateCheckpointAudit ?? null}
          density="compact"
          pinFirstFailureKey={pinned}
        />
      </Box>
      {hp.firstFailedCheckpoint ? (
        <Box
          sx={{
            bgcolor: alpha('#ff7676', 0.12),
            borderRadius: 1.5,
            px: 1,
            py: 0.85,
            mb: 1,
            border: '1px solid',
            borderColor: alpha('#ff7676', 0.45),
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 800, display: 'block', color: '#fff', mb: 0.35 }}
          >
            First FAIL · {hp.firstFailedCheckpoint.gateTitle}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              display: 'block',
              color: 'rgba(255,255,255,0.94)',
              mb: 0.25,
            }}
          >
            {hp.firstFailedCheckpoint.stepLabel}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.88)', lineHeight: 1.45 }}>
            {hp.firstFailedCheckpoint.reason}
          </Typography>
        </Box>
      ) : null}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'rgba(255,255,255,0.82)',
          mb: 0.85,
          fontSize: '0.72rem',
          lineHeight: 1.45,
        }}
      >
        Persisted shorthand for audits:&nbsp;
        <Box component="span" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
          {hp.traceSummaryLine}
        </Box>
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        {hp.checkpointsShort.map((c) => (
          <Stack key={c.gateKey} direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={c.status}
              sx={{
                bgcolor: alpha(statusToColor(theme, c.status), 0.92),
                color: '#fff',
                minWidth: 72,
                height: 20,
              }}
            />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.94)', fontWeight: 500 }}>
              <strong>{c.shortCode}</strong> · {c.title}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 0.75 }} />
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Decision shown:&nbsp;
        <strong>{finalOutcomeLabelStored(row.finalStatus)}</strong>&nbsp;(stored:&nbsp;
        {finalOutcomeTechnical(row.finalStatus)})
        {row.confidenceScore != null ? ` · confidence ${row.confidenceScore}` : ''}
      </Typography>
      {hp.failReasonPreview ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: theme.palette.warning.light }}>
          Blocking note: {hp.failReasonPreview}
        </Typography>
      ) : null}
      <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.76, lineHeight: 1.4 }}>
        ⧉ Opens the drawer; use&nbsp;<strong>Full screen</strong> there when you want room for JSON beside narratives.
      </Typography>
    </Box>
  );
}

export function EmployerVerificationDashboard() {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [latestOnly, setLatestOnly] = useState(true);
  const [finalFilter, setFinalFilter] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState('VERIFIED_AT_DESC');
  const [gateIssue, setGateIssue] = useState<GateIssueFilter>('');
  const [applicationIdQ, setApplicationIdQ] = useState('');
  const [applicantIdQ, setApplicantIdQ] = useState('');
  const [ccidQ, setCcidQ] = useState('');
  const [employerQ, setEmployerQ] = useState('');
  const [verifiedFrom, setVerifiedFrom] = useState('');
  const [verifiedTo, setVerifiedTo] = useState('');
  const [applied, setApplied] = useState({
    applicationIdContains: '',
    applicantIdContains: '',
    ccidContains: '',
    employerNameContains: '',
    verifiedFromIso: '',
    verifiedToIso: '',
    gateIssue: '' as GateIssueFilter,
  });
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailGateFocus, setDetailGateFocus] = useState<GateKey | null>(null);
  const [detailFullscreen, setDetailFullscreen] = useState(false);
  const [overviewApplicantDialog, setOverviewApplicantDialog] = useState<{
    gate: ApiDashboardGateKey;
    outcome: OverviewOutcome;
  } | null>(null);
  const [overviewApplicantPage, setOverviewApplicantPage] = useState(0);
  const [overviewApplicantRowsPerPage, setOverviewApplicantRowsPerPage] = useState(25);
  const [compactTable, setCompactTable] = useState(false);

  const analyticsWindows = useMemo(() => rollingTrailing30dWindows(), []);

  function closeVerificationDetail() {
    setDetailId(null);
    setDetailGateFocus(null);
    setDetailFullscreen(false);
  }

  const summaryQ = useQuery({
    queryKey: ['employerVerificationDashboardSummary'],
    queryFn: () => employerVerificationDashboardApi.summary(),
  });

  const listQ = useQuery({
    queryKey: [
      'employerVerificationDashboardList',
      page,
      rowsPerPage,
      latestOnly,
      finalFilter,
      applied,
      sortBy,
    ],
    queryFn: () =>
      employerVerificationDashboardApi.list({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        latestPerApplicationOnly: latestOnly,
        finalStatus: finalFilter,
        applicationIdContains: applied.applicationIdContains || undefined,
        applicantIdContains: applied.applicantIdContains || undefined,
        ccidContains: applied.ccidContains || undefined,
        employerNameContains: applied.employerNameContains || undefined,
        verifiedFrom: applied.verifiedFromIso || undefined,
        verifiedTo: applied.verifiedToIso || undefined,
        gateIssue: applied.gateIssue || undefined,
        sortBy,
      }),
  });

  const detailQ = useQuery({
    queryKey: ['employerVerificationDashboardDetail', detailId],
    queryFn: () => employerVerificationDashboardApi.detail(detailId!),
    enabled: !!detailId,
  });

  const gateOutcomeCountsQ = useQuery({
    queryKey: ['employerVerificationDashboardGateCounts', latestOnly, finalFilter, applied],
    queryFn: () =>
      employerVerificationDashboardApi.gateOutcomeCounts({
        latestPerApplicationOnly: latestOnly,
        finalStatus: finalFilter,
        applicationIdContains: applied.applicationIdContains || undefined,
        applicantIdContains: applied.applicantIdContains || undefined,
        ccidContains: applied.ccidContains || undefined,
        employerNameContains: applied.employerNameContains || undefined,
        verifiedFrom: applied.verifiedFromIso || undefined,
        verifiedTo: applied.verifiedToIso || undefined,
        gateIssue: applied.gateIssue || undefined,
      }),
  });

  const gateOutcomeApplicantsQ = useQuery({
    queryKey: [
      'employerVerificationDashboardGateApplicants',
      overviewApplicantDialog?.gate,
      overviewApplicantDialog?.outcome,
      overviewApplicantPage,
      overviewApplicantRowsPerPage,
      latestOnly,
      finalFilter,
      applied,
    ],
    queryFn: () =>
      employerVerificationDashboardApi.gateOutcomeApplicants({
        gateKey: overviewApplicantDialog!.gate,
        outcome: overviewApplicantDialog!.outcome,
        limit: overviewApplicantRowsPerPage,
        offset: overviewApplicantPage * overviewApplicantRowsPerPage,
        latestPerApplicationOnly: latestOnly,
        finalStatus: finalFilter,
        applicationIdContains: applied.applicationIdContains || undefined,
        applicantIdContains: applied.applicantIdContains || undefined,
        ccidContains: applied.ccidContains || undefined,
        employerNameContains: applied.employerNameContains || undefined,
        verifiedFrom: applied.verifiedFromIso || undefined,
        verifiedTo: applied.verifiedToIso || undefined,
        gateIssue: applied.gateIssue || undefined,
      }),
    enabled: !!overviewApplicantDialog,
  });

  const dashboardInsightsEmployersQ = useQuery({
    queryKey: [
      'employerVerificationDashboardInsightsEmployers',
      analyticsWindows.current.verifiedFrom,
      analyticsWindows.current.verifiedTo,
    ],
    queryFn: () =>
      employerVerificationAnalyticsApi.employerRollups(analyticsWindows.current, {
        limit: 160,
        offset: 0,
      }),
    staleTime: 120_000,
  });

  const dashboardInsightsCurrentQ = useQuery({
    queryKey: [
      'employerVerificationDashboardInsightsSumCurr',
      analyticsWindows.current.verifiedFrom,
      analyticsWindows.current.verifiedTo,
    ],
    queryFn: () => employerVerificationAnalyticsApi.summary(analyticsWindows.current),
    staleTime: 120_000,
  });

  const dashboardInsightsPriorQ = useQuery({
    queryKey: [
      'employerVerificationDashboardInsightsSumPrev',
      analyticsWindows.prior.verifiedFrom,
      analyticsWindows.prior.verifiedTo,
    ],
    queryFn: () => employerVerificationAnalyticsApi.summary(analyticsWindows.prior),
    staleTime: 120_000,
  });

  const overviewGateApplicants = useMemo(() => {
    const seed = Object.fromEntries(
      GATE_ORDER_PIPE.map((gate) => [
        gate,
        {
          PASS: [] as GateApplicantPreview[],
          FAIL: [] as GateApplicantPreview[],
          REVIEW: [] as GateApplicantPreview[],
        },
      ]),
    ) as Record<GateKey, Record<OverviewOutcome, GateApplicantPreview[]>>;

    for (const row of listQ.data?.items ?? []) {
      for (const gate of GATE_ORDER_PIPE) {
        const outcome = normalizeOverviewOutcome(row.gates[gate]);
        if (!outcome) continue;
        const failCheckpoint = row.gateCheckpointAudit?.[gate]?.find(
          (cp) => cp.status.trim().toUpperCase() === 'FAIL',
        );
        const reviewCheckpoint = row.gateCheckpointAudit?.[gate]?.find(
          (cp) => cp.status.trim().toUpperCase() === 'REVIEW',
        );
        seed[gate][outcome].push({
          id: row.id,
          applicationId: row.applicationId,
          applicantId: row.applicantId,
          ccid: row.ccid,
          employerLegalName: row.employer.legalName,
          gateReason:
            row.gateMessages?.[gate] ||
            failCheckpoint?.reason ||
            reviewCheckpoint?.reason ||
            null,
        });
      }
    }
    return seed;
  }, [listQ.data?.items]);

  const hotspotEmployers = useMemo(() => {
    const items = dashboardInsightsEmployersQ.data?.items ?? [];
    const minVolume = 3;
    return items
      .filter((r) => r.totalVerifications >= minVolume && String(r.entityId ?? '').trim())
      .map((r) => ({
        entityId: String(r.entityId ?? ''),
        legalName: r.legalName ?? String(r.entityId ?? ''),
        rejectedCount: r.rejectedCount,
        totalVerifications: r.totalVerifications,
        rate: r.rejectedCount / r.totalVerifications,
      }))
      .sort((a, b) => b.rate - a.rate || b.rejectedCount - a.rejectedCount)
      .slice(0, 18);
  }, [dashboardInsightsEmployersQ.data?.items]);

  const portfolioTrendNotice = useMemo(() => {
    const cur = dashboardInsightsCurrentQ.data?.outcomes;
    const prev = dashboardInsightsPriorQ.data?.outcomes;
    if (!cur || !prev) return null;
    const c = outcomeBucketShares(cur);
    const p = outcomeBucketShares(prev);
    if (p.total < 20 || c.total < 12) return null;
    const parts: string[] = [];
    if (c.rejectedShare >= p.rejectedShare * 1.2 && c.rejectedShare - p.rejectedShare >= 0.04) {
      parts.push(
        `Reject share climbed from ${Math.round(p.rejectedShare * 100)}% → ${Math.round(c.rejectedShare * 100)}% (rolling 30d vs prior 30d, EMS-wide).`,
      );
    }
    if (c.manualShare >= p.manualShare * 1.25 && c.manualShare - p.manualShare >= 0.04) {
      parts.push(
        `Manual-review share climbed from ${Math.round(p.manualShare * 100)}% → ${Math.round(c.manualShare * 100)}%.`,
      );
    }
    return parts.length ? parts.join(' ') : null;
  }, [dashboardInsightsCurrentQ.data?.outcomes, dashboardInsightsPriorQ.data?.outcomes]);

  const activeFilterSummary = useMemo(() => {
    const bits: string[] = [];
    bits.push(latestOnly ? 'Latest row / application' : 'All verification rows');
    bits.push(finalFilter ? `Final: ${finalFilter}` : 'Final: any');
    if (applied.gateIssue === 'ANY_FAIL') bits.push('Gate filter: any FAIL');
    else if (applied.gateIssue === 'ANY_REVIEW') bits.push('Gate filter: any REVIEW');
    if (applied.applicationIdContains.trim()) bits.push(`App ⊃ "${applied.applicationIdContains.trim()}"`);
    if (applied.applicantIdContains.trim()) bits.push(`Applicant ⊃ "${applied.applicantIdContains.trim()}"`);
    if (applied.ccidContains.trim()) bits.push(`CCID ⊃ "${applied.ccidContains.trim()}"`);
    if (applied.employerNameContains.trim()) bits.push(`Employer ⊃ "${applied.employerNameContains.trim()}"`);
    if (applied.verifiedFromIso.trim()) bits.push(`From ${applied.verifiedFromIso.trim()}`);
    if (applied.verifiedToIso.trim()) bits.push(`To ${applied.verifiedToIso.trim()}`);
    return bits.join(' · ');
  }, [applied, finalFilter, latestOnly]);

  const outcomeQuickFilters = useMemo(() => {
    return (
      summaryQ.data?.listControls?.outcomeQuickFilters ?? [
        { finalStatus: null, label: 'All outcomes' },
        { finalStatus: 'VERIFIED', label: 'Verified' },
        { finalStatus: 'REJECTED', label: 'Rejected' },
        { finalStatus: 'MANUAL_REVIEW', label: 'Manual review' },
        { finalStatus: 'UNKNOWN', label: 'Unknown' },
      ]
    );
  }, [summaryQ.data?.listControls?.outcomeQuickFilters]);

  const sortOptionsList = useMemo(() => {
    return (
      summaryQ.data?.listControls?.sortOptions ?? [
        {
          value: 'VERIFIED_AT_DESC',
          label: 'Latest verified first',
          description: 'Operations default ordering.',
        },
      ]
    );
  }, [summaryQ.data?.listControls?.sortOptions]);

  const gateIssueFilterOptionsList = useMemo(() => {
    return (
      summaryQ.data?.listControls?.gateIssueFilterOptions ?? [
        { value: '' as const, label: 'Any gate trajectory', description: '' },
        { value: 'ANY_FAIL', label: 'Any gate failed', description: '' },
        { value: 'ANY_REVIEW', label: 'Any gate — manual review', description: '' },
      ]
    );
  }, [summaryQ.data?.listControls?.gateIssueFilterOptions]);

  const outcomeTilesData: DashboardOutcomeTile[] = summaryQ.data?.outcomeTiles ?? [];

  const tileMatchesFilter = (tile: DashboardOutcomeTile) =>
    tile.finalStatus != null && finalFilter === tile.finalStatus;

  const applySearch = () => {
    setPage(0);
    setApplied({
      applicationIdContains: applicationIdQ.trim(),
      applicantIdContains: applicantIdQ.trim(),
      ccidContains: ccidQ.trim(),
      employerNameContains: employerQ.trim(),
      verifiedFromIso: verifiedFrom.trim(),
      verifiedToIso: verifiedTo.trim(),
      gateIssue,
    });
  };

  return (
    <Box>
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          borderRadius: 2,
          '&:before': { display: 'none' },
          border: 1,
          borderColor: 'divider',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 56 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              How employment verification runs (gates & checkpoints)
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900, lineHeight: 1.65 }}>
            Every attempt walks the checkpoints in the fixed order printed on the cards. New verifier runs also persist each
            gate’s internal steps (registry lookup, address verification, etc.), so the drawer shows exact pass / fail / review
            reasons instead of a single vague line.&nbsp;
            Row ⧉ opens the record; failures show the first failing checkpoint in the row hint and in the drawer’s “Checkpoint detail”
            block. Optional full-screen keeps JSON beside that layout.
          </Typography>
          <Grid container spacing={2}>
            {(summaryQ.data?.gateFlowSteps ?? []).map((step) => {
              const gate = (GATE_ORDER_PIPE as readonly string[]).includes(step.gateKey)
                ? (step.gateKey as GateKey)
                : null;
              return (
                <Grid key={step.gateKey} size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                        {step.shortCode} — {step.gateKey.replace(/_/g, ' ')}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.62, fontSize: '0.9rem' }}>
                        {step.whatHappens}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ display: 'block', mt: 1.25, color: 'text.secondary', fontSize: '0.8125rem', lineHeight: 1.55 }}
                      >
                        How to read results: {step.outcomesExplained}
                      </Typography>
                      {gate ? (
                        <Box sx={{ mt: 1.25 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            Applicants across current filters — hover for quick sample, click chip for full paginated list
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {OVERVIEW_OUTCOMES.map((outcome) => {
                              const previewRows = overviewGateApplicants[gate][outcome];
                              const globalCount = gateOutcomeCountsQ.data?.gates?.[gate]?.[outcome] ?? previewRows.length;
                              const chipColor =
                                outcome === 'PASS' ? 'success' : outcome === 'FAIL' ? 'error' : 'warning';
                              return (
                                <Tooltip
                                  key={`${step.gateKey}-${outcome}`}
                                  arrow
                                  placement="top"
                                  title={
                                    <Box sx={{ maxWidth: 420 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.6 }}>
                                        {step.title} · {outcome} · {globalCount} applicant(s)
                                      </Typography>
                                      {previewRows.length === 0 ? (
                                        <Typography variant="caption">
                                          No preview rows in current page. Click to fetch full filtered list from backend.
                                        </Typography>
                                      ) : (
                                        <Stack spacing={0.35}>
                                          {previewRows.slice(0, 8).map((entry) => (
                                            <Typography key={`${entry.id}-${outcome}`} variant="caption" sx={{ display: 'block', lineHeight: 1.45 }}>
                                              {entry.applicationId} · {entry.applicantId}
                                              {entry.ccid ? ` · ${entry.ccid}` : ''}
                                              {entry.gateReason ? ` — ${entry.gateReason}` : ''}
                                            </Typography>
                                          ))}
                                          {previewRows.length > 8 || globalCount > previewRows.length ? (
                                            <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                              {globalCount > previewRows.length
                                                ? `${globalCount} total across filters (click chip)`
                                                : `+${previewRows.length - 8} more (click chip)`}
                                            </Typography>
                                          ) : null}
                                        </Stack>
                                      )}
                                    </Box>
                                  }
                                >
                                  <Chip
                                    size="small"
                                    color={chipColor}
                                    label={`${outcome} (${globalCount})`}
                                    onClick={() => {
                                      setOverviewApplicantPage(0);
                                      setOverviewApplicantDialog({ gate, outcome });
                                    }}
                                    sx={{ fontWeight: 800, cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Stack>
                        </Box>
                      ) : null}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
              Applicant result mapping (per gate)
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
              <Chip size="small" color="success" label="PASS" sx={{ fontWeight: 800 }} />
              <Chip size="small" color="error" label="FAIL" sx={{ fontWeight: 800 }} />
              <Chip size="small" color="warning" label="REVIEW" sx={{ fontWeight: 800 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.55 }}>
              Gate statuses are PASS / FAIL / REVIEW.
              {' '}
              <strong>REVIEW</strong> means automation could not finalize confidently in that gate and needs human judgment;
              this commonly contributes to final
              {' '}
              <strong>MANUAL_REVIEW</strong>
              . To see the exact reason for a specific applicant, open a row record (⧉) and use
              {' '}
              <strong>Gates overview</strong>
              {' '}
              + <strong>All checkpoint scenarios</strong>.
            </Typography>
          </Box>
          {summaryQ.data?.finalDecisionLegend ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Final outcome labels
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(summaryQ.data.finalDecisionLegend).map(([k, txt]) => (
                  <Typography key={k} variant="caption" color="text.secondary" component="div">
                    <strong>{k}:</strong> {txt}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ) : null}
          {summaryQ.isError ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Gate curriculum did not load:&nbsp;
              {messageFromEmployerVerificationHttpError(summaryQ.error)}
            </Alert>
          ) : null}
        </AccordionDetails>
      </Accordion>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: -0.02,
            background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Employee verification
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 480 }}>
          Counts and rows are served from the employer-verification service (EMS employment checks only).
        </Typography>
      </Stack>

      {summaryQ.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={36} />
        </Box>
      ) : summaryQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromEmployerVerificationHttpError(summaryQ.error)}
        </Alert>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {outcomeTilesData.map((tile) => {
            const selected = tileMatchesFilter(tile);
            return (
              <Grid key={tile.id} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  variant="outlined"
                  elevation={0}
                  sx={outcomeTileSx(theme, tile.presentation, selected)}
                >
                  <CardActionArea
                    onClick={() => {
                      setPage(0);
                      setFinalFilter(tile.finalStatus ?? undefined);
                    }}
                    aria-label={`Filter table to ${tile.title}, ${tile.count} rows`}
                  >
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {tile.title}
                      </Typography>
                      <Typography className="ev-tile-count" variant="h3" sx={{ mt: 0.75, fontWeight: 900, lineHeight: 1.1 }}>
                        {tile.count}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, lineHeight: 1.45 }}>
                        {tile.subtitle}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {portfolioTrendNotice ? (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberRoundedIcon fontSize="inherit" />}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            Portfolio signal · EMS-wide (analytics API)
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.35 }}>
            {portfolioTrendNotice}
          </Typography>
        </Alert>
      ) : null}

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: alpha(theme.palette.secondary.main, 0.35) }}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
            <TrendingUpRoundedIcon color="secondary" fontSize="small" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Employer hotspots · reject-heavy (last 30d)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, maxWidth: 720 }}>
                Side-scroll the chips — sorted by reject share among employers with at least three runs. Opens EV Analytics with that entity prefilled so you can dig into rollup + Places intel faster.
              </Typography>
            </Box>
          </Stack>
          {dashboardInsightsEmployersQ.isError ? (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Hotspot strip unavailable:&nbsp;
              {messageFromEmployerVerificationHttpError(dashboardInsightsEmployersQ.error)}
            </Alert>
          ) : dashboardInsightsEmployersQ.isPending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="caption" color="text.secondary">
                Loading rollup slice…
              </Typography>
            </Box>
          ) : hotspotEmployers.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No qualifying employers yet (need volume in the trailing 30d window).
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                py: 0.75,
                mx: -1,
                px: 1,
                scrollSnapType: 'x proximity',
                '& > *': { scrollSnapAlign: 'start', flexShrink: 0 },
              }}
            >
              {hotspotEmployers.map((h) => (
                <Chip
                  key={h.entityId}
                  component={RouterLink}
                  to={`/employer-verification/analytics?${new URLSearchParams({
                    entityId: h.entityId,
                    verifiedFrom: analyticsWindows.current.verifiedFrom,
                    verifiedTo: analyticsWindows.current.verifiedTo,
                  }).toString()}`}
                  clickable
                  variant="filled"
                  color="secondary"
                  label={
                    <Box sx={{ textAlign: 'left', py: 0.25, maxWidth: 220 }}>
                      <Typography variant="caption" sx={{ fontWeight: 900, display: 'block', lineHeight: 1.3 }}>
                        {h.legalName}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.68rem', display: 'block' }}>
                        {Math.round(h.rate * 100)}% reject · {h.rejectedCount}/{h.totalVerifications}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    borderRadius: 2,
                    height: 'auto',
                    '& .MuiChip-label': { px: 1.1, py: 0.5, whiteSpace: 'normal' },
                  }}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Filters & sorting
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            Outcome shortcuts stay in sync with the summary API. Text filters apply after you click Apply filters.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {outcomeQuickFilters.map((opt) => {
              const isAll = opt.finalStatus === null || opt.finalStatus === undefined;
              const selected = isAll ? finalFilter === undefined : finalFilter === opt.finalStatus;
              return (
                <Chip
                  key={opt.label}
                  label={opt.label}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => {
                    setPage(0);
                    setFinalFilter(isAll ? undefined : opt.finalStatus ?? undefined);
                  }}
                />
              );
            })}
          </Stack>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Application id contains"
                value={applicationIdQ}
                onChange={(e) => setApplicationIdQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Applicant id contains"
                value={applicantIdQ}
                onChange={(e) => setApplicantIdQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Customer / CCID contains"
                value={ccidQ}
                onChange={(e) => setCcidQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Employer name contains"
                value={employerQ}
                onChange={(e) => setEmployerQ(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" onClick={applySearch}>
                        Apply filters
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Verified after (ISO, e.g. 2026-05-01T00:00:00Z)"
                value={verifiedFrom}
                onChange={(e) => setVerifiedFrom(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Verified before (ISO)"
                value={verifiedTo}
                onChange={(e) => setVerifiedTo(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Gate anomaly</InputLabel>
                <Select
                  label="Gate anomaly"
                  value={gateIssue}
                  onChange={(e) => setGateIssue(e.target.value as GateIssueFilter)}
                >
                  {gateIssueFilterOptionsList.map((go) => (
                    <MenuItem key={go.value || 'any'} value={go.value} title={go.description || undefined}>
                      {go.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort rows by</InputLabel>
                <Select
                  label="Sort rows by"
                  value={sortBy}
                  onChange={(e) => {
                    setPage(0);
                    setSortBy(String(e.target.value));
                  }}
                >
                  {sortOptionsList.map((so) => (
                    <MenuItem key={so.value} value={so.value} title={so.description || undefined}>
                      {so.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 0.5 }} alignItems={{ sm: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={latestOnly}
                  onChange={(_, v) => {
                    setPage(0);
                    setLatestOnly(v);
                  }}
                />
              }
              label="Latest attempt per application (recommended ops default)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={compactTable}
                  onChange={(_, v) => setCompactTable(v)}
                />
              }
              label="Compact table density"
            />
          </Stack>
        </CardContent>
      </Card>

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          py: 0.85,
          px: 1.25,
          mb: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.85 : 0.78),
          backdropFilter: 'blur(12px)',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
          Active listing context
        </Typography>
        <Typography variant="caption" sx={{ lineHeight: 1.55 }}>
          {activeFilterSummary}
        </Typography>
      </Box>

      {listQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromEmployerVerificationHttpError(listQ.error)}
        </Alert>
      ) : null}

      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          borderColor: alpha(
            theme.palette.primary.main,
            theme.palette.mode === 'light' ? 0.22 : 0.35,
          ),
          boxShadow:
            theme.palette.mode === 'light'
              ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.06)}`
              : `0 12px 48px ${alpha('#000', 0.35)}`,
        }}
      >
        <Table
          stickyHeader
          size={compactTable ? 'small' : 'medium'}
          sx={{
            '& .MuiTableCell-root': {
              fontSize: compactTable ? '0.8125rem' : '0.875rem',
              py: compactTable ? 0.65 : 1.25,
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  bgcolor:
                    theme.palette.mode === 'light'
                      ? alpha(theme.palette.background.paper, 0.95)
                      : alpha(theme.palette.background.default, 0.98),
                },
              }}
            >
              <TableCell sx={{ width: 48 }} aria-label="Open detail" />
              <TableCell>
                <Tooltip title={dashboardColumns.decision} placement="top" arrow>
                  <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dashed', borderColor: 'divider', pb: 0.1 }}>
                    Decision
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={dashboardColumns.checkpoints} placement="top" arrow>
                  <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dashed', borderColor: 'divider', pb: 0.1 }}>
                    Checkpoints
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={dashboardColumns.applicationId} placement="top" arrow>
                  <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dashed', borderColor: 'divider', pb: 0.1 }}>
                    Application ID
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={dashboardColumns.applicantCcId} placement="top" arrow>
                  <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dashed', borderColor: 'divider', pb: 0.1 }}>
                    Applicant · Customer ID
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={dashboardColumns.employer} placement="top" arrow>
                  <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dashed', borderColor: 'divider', pb: 0.1 }}>
                    Employer
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title={dashboardColumns.attempt} placement="top" arrow>
                  <Box
                    component="span"
                    sx={{ cursor: 'help', borderBottom: '1px dashed', borderColor: 'divider', pb: 0.1, display: 'inline-block' }}
                  >
                    Attempt
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={dashboardColumns.completed} placement="top" arrow>
                  <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dashed', borderColor: 'divider', pb: 0.1 }}>
                    Completed
                  </Box>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listQ.isPending ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              (listQ.data?.items ?? []).map((row) => (
                <TableRow key={row.id} hover sx={listRowAccentSx(theme, row)}>
                  <TableCell>
                    <Tooltip title="Open verification record (narratives + JSON)">
                      <IconButton
                        size="small"
                        aria-label="Open verification detail drawer"
                        onClick={() => {
                          setDetailGateFocus(null);
                          setDetailId(row.id);
                        }}
                      >
                        <OpenInNewIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={<RowHoverInsight row={row} />}
                      arrow
                      followCursor
                      placement="bottom-start"
                      slotProps={{
                        tooltip: {
                          sx: {
                            bgcolor: alpha(theme.palette.grey[900], 0.94),
                            maxWidth: 520,
                          },
                        },
                        popper: { modifiers: [{ name: 'offset', options: { offset: [8, 12] } }] },
                      }}
                    >
                      <Box component="span">
                        <Tooltip title={`Stored: ${finalOutcomeTechnical(row.finalStatus)}`} placement="top" arrow>
                          <Chip
                            size="small"
                            label={finalOutcomeLabelStored(row.finalStatus)}
                            color={employerVerificationFinalChipColor(row.finalStatus)}
                            sx={{ fontWeight: 700 }}
                          />
                        </Tooltip>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ minWidth: 220, maxWidth: 420 }}>
                    <Box sx={{ display: 'inline-block', width: '100%' }}>
                      <GateResultChipsRow
                        row={row}
                        onGateClick={(gate) => {
                          setDetailGateFocus(gate);
                          setDetailId(row.id);
                        }}
                      />
                      {row.hoverPayload?.firstFailedCheckpoint ? (
                        <Typography
                          component="div"
                          variant="caption"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mt: 0.85,
                            lineHeight: 1.45,
                            color: 'error.main',
                            fontWeight: 600,
                          }}
                        >
                          {row.hoverPayload.firstFailedCheckpoint.stepLabel}: {row.hoverPayload.firstFailedCheckpoint.reason}
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        letterSpacing: 0.01,
                      }}
                    >
                      {row.applicationId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                      }}
                    >
                      {row.applicantId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mt: 0.25 }}>
                      {row.ccid ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.employer.legalName ?? '—'}
                    </Typography>
                    {row.employer.ceid ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mt: 0.25 }}>
                        Registry ID {row.employer.ceid}
                      </Typography>
                    ) : null}
                    {row.entityId?.trim() ? (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.35 }}>
                        <Box
                          component={RouterLink}
                          to={`/employer-verification/analytics?${new URLSearchParams({
                            entityId: row.entityId.trim(),
                            verifiedFrom: analyticsWindows.current.verifiedFrom,
                            verifiedTo: analyticsWindows.current.verifiedTo,
                          }).toString()}`}
                          sx={{
                            fontWeight: 700,
                            textDecoration: 'none',
                            color: 'secondary.main',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          Employer analytics ⧉
                        </Box>
                      </Typography>
                    ) : null}
                    {row.finalStatus?.trim().toUpperCase() === 'REJECTED' &&
                    row.failReason &&
                    row.failReason.trim() ? (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mt: 0.65,
                          fontWeight: 700,
                          lineHeight: 1.45,
                        }}
                      >
                        {row.failReason.trim()}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.attemptNumber ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">
                      {row.verifiedAt ? new Date(row.verifiedAt).toLocaleString() : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={listQ.data?.total ?? 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <Drawer
        anchor="right"
        open={!!detailId}
        onClose={closeVerificationDetail}
        PaperProps={{ sx: { width: { xs: 1, sm: 520, md: 640 }, maxWidth: '100vw' } }}
      >
        {detailQ.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : detailQ.isError ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{messageFromEmployerVerificationHttpError(detailQ.error)}</Alert>
          </Box>
        ) : detailQ.data ? (
          <Box sx={{ overflow: 'auto', height: '100%' }}>
            <EmployerVerificationDetailPane
              layout="drawer"
              data={detailQ.data}
              focusedGate={detailGateFocus}
              onClose={closeVerificationDetail}
              onRequestFullscreen={() => setDetailFullscreen(true)}
            />
          </Box>
        ) : null}
      </Drawer>

      <Dialog fullScreen open={detailFullscreen && !!detailId && !!detailQ.data} onClose={() => setDetailFullscreen(false)}>
        {detailQ.data ? (
          <EmployerVerificationDetailPane
            layout="fullscreen"
            data={detailQ.data}
            focusedGate={detailGateFocus}
            onClose={() => setDetailFullscreen(false)}
          />
        ) : null}
      </Dialog>

      <Dialog
        open={!!overviewApplicantDialog}
        onClose={() => {
          setOverviewApplicantDialog(null);
          setOverviewApplicantPage(0);
        }}
        fullWidth
        maxWidth="md"
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            {overviewApplicantDialog
              ? `${gateMeta[overviewApplicantDialog.gate].title} · ${overviewApplicantDialog.outcome} applicants`
              : 'Gate applicants'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            From all applicants matching current filters. Click an applicant to open this gate’s detailed checkpoint output.
          </Typography>
          <Stack spacing={1} sx={{ maxHeight: '65vh', overflow: 'auto' }}>
            {(gateOutcomeApplicantsQ.data?.items ?? []).map((entry) => (
              <Card key={entry.id} variant="outlined">
                <CardContent sx={{ py: 1.15, '&:last-child': { pb: 1.15 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {entry.applicationId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Applicant {entry.applicantId}
                        {entry.ccid ? ` · CCID ${entry.ccid}` : ''}
                        {entry.employerLegalName ? ` · ${entry.employerLegalName}` : ''}
                      </Typography>
                      {entry.gateReason ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6, lineHeight: 1.45 }}>
                          {entry.gateReason}
                        </Typography>
                      ) : null}
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        if (!overviewApplicantDialog) return;
                        setDetailGateFocus(overviewApplicantDialog.gate);
                        setDetailId(entry.id);
                        setOverviewApplicantDialog(null);
                      }}
                    >
                      Open gate result
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {gateOutcomeApplicantsQ.isPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : null}
            {gateOutcomeApplicantsQ.isError ? (
              <Alert severity="error">{messageFromEmployerVerificationHttpError(gateOutcomeApplicantsQ.error)}</Alert>
            ) : null}
            {!gateOutcomeApplicantsQ.isPending &&
            !gateOutcomeApplicantsQ.isError &&
            (gateOutcomeApplicantsQ.data?.items.length ?? 0) === 0 ? (
              <Alert severity="info">No applicants for this gate outcome with current filters.</Alert>
            ) : null}
          </Stack>
          <TablePagination
            component="div"
            count={gateOutcomeApplicantsQ.data?.total ?? 0}
            page={overviewApplicantPage}
            onPageChange={(_e, p) => setOverviewApplicantPage(p)}
            rowsPerPage={overviewApplicantRowsPerPage}
            onRowsPerPageChange={(e) => {
              setOverviewApplicantRowsPerPage(Number(e.target.value));
              setOverviewApplicantPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button
              onClick={() => {
                setOverviewApplicantDialog(null);
                setOverviewApplicantPage(0);
              }}
            >
              Close
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
